import type {
  OrderFilter,
  OrderFiltersValue,
  OrderReportSummary,
  OrdersResponse,
  OrderUrlFilters
} from './types';
import type {HeaderDataPayload} from '@ds/types';

import {CloseOutlined, SearchOutlined} from '@ant-design/icons';
import {IconButton, Input, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useUrlFilters} from '@router';
import {toast} from '@Toast';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useAppDispatch, useAppSelector} from 'store';
import {getBranches} from 'store/slices/dashboard/thunk';
import {getOrders, getOrdersReportNew} from 'store/slices/order/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import OrderCard from './components/OrderCard/OrderCard';
import OrdersEmptyState from './components/OrdersEmptyState/OrdersEmptyState';
import OrdersFilters from './components/OrdersFilters/OrdersFilters';
import OrdersSummary from './components/OrdersSummary/OrdersSummary';
import {ORDER_FILTER_SCHEMA, ORDER_PAGE_SIZE} from './constants';
import {createOrderFilterState, createOrderUrlFilters, validateDateRange} from './utils';

type OrdersProps = {
  headerData?: (data: HeaderDataPayload) => void;
};

const Orders: React.FC<OrdersProps> = ({headerData}) => {
  const dispatch = useAppDispatch();
  const {
    filters: urlFilters,
    setFilters: setUrlFilters,
    clearFilters: clearUrlFilters
  } = useUrlFilters<OrderUrlFilters>({
    schema: ORDER_FILTER_SCHEMA
  });
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const selectedVendor = useAppSelector((state) => state.dashboard.selectedVendor);
  const vendorId = selectedVendor?.id ?? getSelectedVendorFromStorage()?.id;
  const {orders, orderReports, loading} = useAppSelector((state) => state.order) as {
    orders: OrdersResponse;
    orderReports?: OrderReportSummary;
    loading: number;
  };
  const [page, setPage] = useState(1);
  const [view, setView] = useState<'orders' | 'search'>('orders');
  const [search, setSearch] = useState('');
  const urlFilterState = useMemo(() => createOrderFilterState(urlFilters), [urlFilters]);
  const filters = urlFilterState.filters;
  const filterValues = urlFilterState.values;
  const requestFilterValues = useMemo<OrderFiltersValue>(() => {
    if (filterValues.start_date && filterValues.end_date) {
      return filterValues;
    }

    const today = new Date().toISOString().split('T')[0];
    return {
      ...filterValues,
      start_date: today,
      end_date: today
    };
  }, [filterValues]);

  const fetchOrders = useCallback(
    (requestedPage: number, values: OrderFiltersValue) => {
      if (!vendorId) {
        return;
      }
      void dispatch(
        getOrders({
          vendor_id: vendorId,
          page: requestedPage,
          page_size: ORDER_PAGE_SIZE,
          ...values
        })
      );
      if (requestedPage === 1) {
        void dispatch(
          getOrdersReportNew({
            vid: vendorId,
            ...values
          })
        );
      }
    },
    [dispatch, vendorId]
  );

  useEffect(() => {
    if (!vendorId) {
      void dispatch(getBranches({page: 0, pageSize: 10}));
      return;
    }
    fetchOrders(1, requestFilterValues);
  }, [dispatch, fetchOrders, requestFilterValues, vendorId]);

  useEffect(() => {
    headerData?.({action: () => setView('search')});
  }, [headerData]);

  useEffect(
    () => () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    },
    []
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    if (!vendorId) {
      return;
    }

    if (value.length >= 3) {
      searchTimer.current = setTimeout(() => {
        setPage(1);
        void dispatch(
          getOrders({
            vendor_id: vendorId,
            page: 1,
            page_size: ORDER_PAGE_SIZE,
            search_query: value
          })
        );
      }, 500);
    } else if (value.length === 0) {
      setPage(1);
      fetchOrders(1, requestFilterValues);
    }
  };

  const handleCloseSearch = () => {
    setView('orders');
    setSearch('');
    setPage(1);
    fetchOrders(1, requestFilterValues);
  };

  const applyFilters = (nextFilters: OrderFilter[]) => {
    const customDate = nextFilters
      .find((filter) => filter.name === 'date')
      ?.items?.find((item) => item.key === 'customDate' && item.selected)?.date;
    if (customDate && (!customDate.start_date || !customDate.end_date)) {
      toast('تاریخ شروع و پایان را به صورت معتبر وارد کنید');
      return false;
    }

    const selectedDate = nextFilters
      .find((filter) => filter.name === 'date')
      ?.items?.find((item) => item.selected)?.date;
    const dateError = selectedDate
      ? validateDateRange(selectedDate.start_date, selectedDate.end_date)
      : '';
    if (dateError) {
      toast(dateError);
      return false;
    }
    setPage(1);
    setUrlFilters(createOrderUrlFilters(nextFilters), {replace: true});
    return true;
  };

  const clearFilter = (filter: OrderFilter) => {
    if (filter.name === 'date') {
      clearUrlFilters(['start_date', 'end_date'], {replace: true});
    } else {
      clearUrlFilters(filter.name as keyof OrderUrlFilters, {replace: true});
    }
    setPage(1);
  };

  const currentOrders = Array.isArray(orders?.orders) ? orders.orders : [];
  const isInitialLoading = loading > 0 && currentOrders.length === 0;
  const hasMore = currentOrders.length < (orders?.pagination?.total ?? 0);
  const header =
    view === 'orders' ? (
      <>
        <OrdersSummary data={orderReports} />
        <OrdersFilters
          filters={filters}
          values={filterValues}
          onApply={(_, nextFilters) => applyFilters(nextFilters ?? filters)}
          onClear={clearFilter}
        />
      </>
    ) : (
      <div className="flex items-center pe-4 py-2">
        <IconButton
          aria-label="بستن جستجو"
          variant="ghost"
          color="secondary"
          size="lg"
          onClick={handleCloseSearch}
        >
          <CloseOutlined aria-hidden />
        </IconButton>
        <Input
          value={search}
          prefix={<SearchOutlined className="text-xl" aria-hidden />}
          placeholder="جستجو..."
          onChange={(event) => handleSearch(event.target.value)}
        />
      </div>
    );

  return (
    <PageLayout contentId="orders-list" header={header}>
      {isInitialLoading ? (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Loading size="large" color="primary" />
        </div>
      ) : currentOrders.length === 0 ? (
        <OrdersEmptyState />
      ) : (
        <InfiniteScroll
          dataLength={currentOrders.length}
          scrollableTarget="orders-list"
          next={() => {
            const nextPage = page + 1;
            setPage(nextPage);
            if (view === 'search') {
              if (vendorId) {
                void dispatch(
                  getOrders({
                    vendor_id: vendorId,
                    page: nextPage,
                    page_size: ORDER_PAGE_SIZE,
                    search_query: search
                  })
                );
              }
            } else {
              fetchOrders(nextPage, requestFilterValues);
            }
          }}
          hasMore={hasMore}
          scrollThreshold={0.6}
          loader={
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          }
        >
          <div className="flex flex-col">
            {currentOrders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default Orders;
